import { z } from 'zod';
import { emailSchema, paginationSchema } from './schemas.js';

/** Every state-changing Super Admin action carries a written justification. */
export const adminReasonSchema = z
  .string()
  .trim()
  .min(10, 'Explain the reason in at least 10 characters')
  .max(500);

export const stepUpSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});

export const grantPlatformAdminSchema = z.object({
  email: emailSchema,
  reason: adminReasonSchema,
});

export const revokePlatformAdminSchema = z.object({ reason: adminReasonSchema });

const isoDate = z.iso.datetime({ offset: true });

export const platformAuditQuerySchema = paginationSchema.extend({
  action: z
    .string()
    .trim()
    .max(100)
    .regex(/^[a-z_.]+$/)
    .optional(),
  actorUserId: z.string().length(26).optional(),
  targetTenantId: z.string().length(26).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});

export interface PlatformAdminDto {
  id: string;
  email: string;
  name: string;
  mfaEnabled: boolean;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
}

export interface PlatformMeDto {
  user: { id: string; email: string; name: string };
  /** True while a recent second-factor confirmation allows privileged changes. */
  stepUpActive: boolean;
  stepUpExpiresAt: string | null;
}

export interface PlatformAuditLogDto {
  id: string;
  action: string;
  actorType: 'user' | 'system' | 'api_key' | 'platform_admin';
  actorUserId: string | null;
  actorLabel: string;
  targetTenantId: string | null;
  targetType: string | null;
  targetId: string | null;
  reason: string;
  requestId: string | null;
  ipAddress: string | null;
  before: unknown;
  after: unknown;
  createdAt: string;
}

// ── Overview dashboard (Phase 7d-2) ────────────────────────────────────────

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** Longest window the overview will aggregate (daily buckets). */
export const ADMIN_OVERVIEW_MAX_DAYS = 366;

export const adminOverviewQuerySchema = z
  .object({
    range: z.enum(['7d', '30d', '90d', 'custom']).default('30d'),
    from: isoDay.optional(),
    to: isoDay.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.range !== 'custom') return;
    if (!v.from || !v.to) {
      ctx.addIssue({ code: 'custom', message: 'Custom ranges need from and to', path: ['from'] });
      return;
    }
    const from = Date.parse(`${v.from}T00:00:00Z`);
    const to = Date.parse(`${v.to}T00:00:00Z`);
    if (Number.isNaN(from) || Number.isNaN(to)) {
      ctx.addIssue({ code: 'custom', message: 'Invalid date', path: ['from'] });
    } else if (from > to) {
      ctx.addIssue({ code: 'custom', message: 'From must not be after to', path: ['from'] });
    } else if ((to - from) / 86_400_000 + 1 > ADMIN_OVERVIEW_MAX_DAYS) {
      ctx.addIssue({
        code: 'custom',
        message: `Choose at most ${ADMIN_OVERVIEW_MAX_DAYS} days`,
        path: ['to'],
      });
    }
  });

export interface MoneyByCurrency {
  currency: string;
  /** Minor units (paise / cents). Currencies are never added together. */
  amountMinor: number;
}

export interface AdminOverviewDto {
  range: { from: string; to: string; days: number; previousFrom: string; previousTo: string };
  generatedAt: string;
  /** Point-in-time figures, not affected by the date range. */
  snapshot: {
    merchants: { total: number; active: number; suspended: number; closed: number };
    /** Each merchant is in exactly one bucket; see `definitions`. */
    plans: { trial: number; paid: number; free: number; internal: number };
    activeSubscriptions: number;
    mrr: MoneyByCurrency[];
    byPlan: { label: string; merchants: number }[];
  };
  /** Figures for the selected range, with the immediately preceding window for comparison. */
  period: {
    newMerchants: { value: number; previous: number };
    newUsers: { value: number; previous: number };
    payments: {
      /** Paid payments only, per currency. `netMinor` excludes GST where an invoice recorded it. */
      collected: {
        currency: string;
        grossMinor: number;
        netMinor: number;
        taxMinor: number;
        count: number;
      }[];
    };
    whatsapp: {
      sent: { value: number; previous: number };
      queued: number;
      delivered: number;
      read: number;
      failed: number;
    };
    automation: { runs: { value: number; previous: number }; failed: number };
    ai: {
      runs: { value: number; previous: number };
      replies: number;
      blocked: number;
      inputTokens: number;
      outputTokens: number;
    };
    calls: {
      sessions: { value: number; previous: number };
      blocked: number;
      completed: number;
      failed: number;
      estimatedCostMinor: number;
    };
    conversion: {
      cohort: number;
      converted: number;
      stillTrialing: number;
      /** converted / cohort, or null when the cohort is empty. */
      rate: number | null;
    };
  };
  series: {
    /** One entry per UTC day in the range. */
    days: string[];
    merchantsTotal: number[];
    newMerchants: number[];
    newUsers: number[];
    revenue: { currency: string; netMinor: number[] }[];
    whatsapp: { sent: number[]; failed: number[] };
    automationRuns: number[];
    aiTokens: { input: number[]; output: number[] };
  };
  definitions: Record<string, string>;
}
